import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import cn from 'classnames';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import { Button } from '@material-ui/core';
import BulkDownloadLink from '../../BulkDownloadButton/BulkDownloadLink';
import Spinner from '../../Loaders/Spinner';
import MainButton from '../../MUI/MainButton';
import { efesGray } from '../../../theme';
import useDeviceResize from '../../../hooks/useDeviceResize';
import MainTextField from '../../MUI/MainTextField';

import successContract from '../../../styles/svgs/succesContract.svg';
import styles from '../PaymentConfirmation.module.scss';
import useStyles from '../makeStyles';

const SuccessContent = ({
  pdf,
  remainingRequest,
  errors,
  onSubmit,
  handleSubmit,
  setValue,
  getValues,
  clearError,
  sendingEmail,
  hasPdf,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const spinnerSize = useDeviceResize('lg') ? 80 : 50;

  return (
    <div id={styles.confirmationScreen}>
      <div className={styles.paymentStatusBlock}>
        <img className={styles.icon} src={successContract} alt="" />
        <div>
          <div className={classes.statusTitle}>{t('paymentConfirmation.contractSuccessText')}</div>
          <div className={classes.contractSuccessSubText}>
            <div className={classes.subText}>{t('paymentConfirmation.contractSuccessSubText')}</div>
            {pdf.length > 1 && <BulkDownloadLink pdf={pdf} />}
          </div>

          {pdf.length === 0 && remainingRequest > 0 && hasPdf && (
            <div className={classes.loadingText}>
              <Spinner color={efesGray} width={spinnerSize} height={spinnerSize} />
              <p>{t('paymentConfirmation.pdfWait')}</p>
            </div>
          )}

          {!pdf.length && remainingRequest === 0 && (
            <div className={classes.pdfFailedStatus}>
              <p>{t('paymentConfirmation.pdfError')}</p>
              <MainButton variant="outlined" color="primary" href="/">
                {t('paymentConfirmation.finish')}
              </MainButton>
            </div>
          )}

          {pdf.length > 0 && (
            <form onSubmit={handleSubmit(onSubmit)}>
              <MainTextField
                variant="outlined"
                size="small"
                className={classes.input}
                name="emails"
                placeholder={t('paymentConfirmation.email')}
                onChange={(event) => {
                  setValue('emails', event.target.value);
                  if (!getValues().emails) {
                    clearError(undefined);
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <MainButton
                      size="small"
                      variant="text"
                      color="primary"
                      type="submit"
                      disabled={sendingEmail}
                    >
                      {t('paymentConfirmation.send')}
                    </MainButton>
                  ),
                }}
              />
              <div
                className={cn(styles.errorMessage, {
                  [styles.errMsgHidden]: !errors.emails,
                })}
              >
                {t('paymentConfirmation.invalidEmail')}
              </div>
            </form>
          )}
        </div>
      </div>

      {pdf.length > 0 && (
        <div>
          <div className={styles.pdfs}>
            {pdf.map((item) => (
              <div className={styles.pdfBlock} key={item.url}>
                <div className={styles.pdfHeader}>
                  <Button
                    color="primary"
                    type="outlined"
                    href={item.url}
                    rel="noopener noreferrer"
                    download
                    target="_blank"
                    startIcon={<CloudDownloadIcon />}
                    className={classes.downloadButton}
                  >
                    {t('paymentConfirmation.downloadContract')}
                  </Button>
                </div>
                <object
                  className={styles.pdfView}
                  aria-label="contract"
                  data={item.url}
                  type="application/pdf"
                />
              </div>
            ))}
          </div>
          <div className={styles.pdfs}>
            <MainButton variant="outlined" color="primary" href="/">
              {t('paymentConfirmation.finish')}
            </MainButton>
          </div>
        </div>
      )}
    </div>
  );
};

SuccessContent.propTypes = {
  pdf: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  remainingRequest: PropTypes.number.isRequired,
  errors: PropTypes.shape({
    emails: PropTypes.shape({}),
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  getValues: PropTypes.func.isRequired,
  clearError: PropTypes.func.isRequired,
  sendingEmail: PropTypes.bool.isRequired,
  hasPdf: PropTypes.bool.isRequired,
};

export default SuccessContent;
